import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAttachTickets, useDetachTickets, useTickets } from '@/lib/adminApi';
import type { TimeSessionSummary, TicketSummary } from '@ventin/shared';

/**
 * SessionTicketsPanel — TKT-05: attach/detach tickets on an active session.
 *
 * Rendered on TicketDetailPage when the live session is RUNNING or PAUSED.
 * Lists current session ticketIds with per-row Detach buttons.
 * Includes a debounced search input (300ms) for attaching additional OPEN tickets.
 */

interface Props {
  session: TimeSessionSummary;
  currentTicketId: string;
}

export function SessionTicketsPanel({ session, currentTicketId: _currentTicketId }: Props) {
  const attachMut = useAttachTickets();
  const detachMut = useDetachTickets();

  // Search for additional tickets to attach
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Fetch open tickets for search (client-side filter)
  const { data: ticketsData } = useTickets({ status: 'OPEN' as TicketSummary['status'] });
  const allOpenTickets = ticketsData?.tickets ?? [];

  // Filter client-side by title match and exclude already-attached tickets
  const suggestions = useMemo(() => {
    if (!debouncedQuery.trim()) return [];
    const lower = debouncedQuery.toLowerCase();
    return allOpenTickets.filter(
      (t) => t.title.toLowerCase().includes(lower) && !session.ticketIds.includes(t.id),
    );
  }, [debouncedQuery, allOpenTickets, session.ticketIds]);

  function handleDetach(ticketId: string) {
    detachMut.mutate({ sessionId: session.id, ticketIds: [ticketId] });
  }

  function handleAttach(ticketId: string) {
    attachMut.mutate({ sessionId: session.id, ticketIds: [ticketId] });
    setSearchQuery('');
    setDebouncedQuery('');
  }

  return (
    <div className="rounded-md border bg-background p-4">
      <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
        Session Tickets
      </h3>

      {/* Attached tickets list */}
      <ul className="space-y-2 mb-4">
        {session.ticketIds.map((tid) => (
          <li key={tid} className="flex items-center justify-between gap-2 text-sm">
            <span className="font-mono text-xs text-muted-foreground truncate flex-1">{tid}</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => handleDetach(tid)}
              disabled={detachMut.isPending}
            >
              Detach
            </Button>
          </li>
        ))}
      </ul>

      {/* Attach search */}
      <div className="space-y-2">
        <input
          type="text"
          className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          placeholder="Attach another ticket by title…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {suggestions.length > 0 && (
          <ul className="space-y-1 border rounded-md p-2">
            {suggestions.map((ticket) => (
              <li key={ticket.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate flex-1">{ticket.title}</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => handleAttach(ticket.id)}
                  disabled={attachMut.isPending}
                >
                  Attach
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
